/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Divider, Grid, Typography } from '@mui/material';
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { PatternFormat } from 'react-number-format';
import { useNavigate, useParams } from 'react-router-dom';

import {
  convertReportGoal,
  convertReportSource,
} from '../../../../api/enumerations';
import {
  activateElementErrorReport,
  finishElementErrorReport,
  updateErrorReport,
} from '../../../../api/errorReport';
import {
  ErrorElementData,
  ErrorElementPictures,
  ErrorReportData,
} from '../../../../api/errorReport/types';
import {
  CustomTextField,
  MultilineTextField,
  NumericTextField,
  PreffixNumericTextField,
  SelectTextField,
} from '../../../../components/CustomInput';
import StandardMap from '../../../../components/Maps/StandardMap';
import MapSearchButton from '../../../../components/MapSearchButton';
import { BoxContainer } from '../../../../components/UI/Box';
import {
  StyledTextField,
  TitleTypography,
} from '../../../../components/UI/Typography';
import { Constants } from '../../../../constants/errorReport';
import {
  IconEditDocumentMS,
  IconFactCheckMS,
  IconInventoryMS,
  IconPhotoLibraryMS,
  IconWarningMS,
} from '../../../../constants/icons';
import {
  independentStructuresGroup,
  privateUnitsGroup,
  selectPropertyType,
} from '../../../../constants/selectOptions';
import { GlobalContext } from '../../../../context/global';
import { convertDate, formatPhone } from '../../../../helpers';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useGeneral from '../../../../hooks/useGeneral';
import useSearchCep from '../../../../hooks/useSearchCep';
import {
  CreatedStyle,
  DescriptionTypography,
  FlexGrid,
  OSNumber,
  SectionBox,
  SectionTitle,
  SubmitBox,
  TitleBox,
} from '../styles';
import AllPicturesSlider from './AllPicturesSlider';
import FinishElementDialog from './FinishElementDialog';
import {
  CoordContainer,
  FlexBox,
  BoldTypography,
  OutlinedButton,
  DownloadButton,
  StyledCardMedia,
} from './styles';

type LatLngLiteral = google.maps.LatLngLiteral;
interface ElementProps {
  errorReport: ErrorReportData;
  elementData: ErrorElementData;
  errorFindings: string | undefined;
}

export function Element({
  errorReport,
  elementData,
  errorFindings,
}: ElementProps): JSX.Element {
  const [pictures, setPictures] = useState<ErrorElementPictures[]>([]);
  const [address, setAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [cep, setCep] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [uf, setUf] = useState('');
  const [propertyType, setPropertyType] = useState(0);
  const [privateArea, setPrivateArea] = useState(0);
  const [landArea, setLandArea] = useState(0);
  const [age, setAge] = useState(0);
  const [parkingLots, setParkingLots] = useState(0);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [rooms, setRooms] = useState(0);
  const [communityValue, setCommunityValue] = useState(0);
  const [advertiser, setAdvertiser] = useState('');
  const [advertiserContact, setAdvertiserContact] = useState('');
  const [propertyLink, setPropertyLink] = useState('');
  const [description, setDescription] = useState('');
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>();
  const [searchMap, setSearchMap] = useState(false);
  const [isAddressEdited, setIsAddressEdited] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [findings, setFindings] = useState('');

  const isIndependentStructure = independentStructuresGroup.has(propertyType);
  const isPrivateUnit = privateUnitsGroup.has(propertyType);

  const navigate = useNavigate();
  const { id } = useParams();
  const reportId = parseInt(id as string, 10);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { handleDownloadFile } = useGeneral();
  const { getErrorMessage } = useErrorMessage();
  const { handleSearchCep } = useSearchCep();

  const getDataCallback = useCallback(async () => {
    setCep(elementData.zipcode || '');
    setAddress(elementData.street);
    setAddressNumber(elementData.number);
    setCity(elementData.city);
    setDistrict(elementData.district);
    setPropertyType(errorReport.real_estate_kind);
    setPrivateArea(elementData.area);
    setAge(elementData.age);
    setAddressComplement(elementData.address_complement || '');
    setUf(elementData.state || '');
    setParkingLots(elementData.parking_spaces);
    setRooms(elementData.bedrooms);
    setSellingPrice(elementData.sell_price);
    setCommunityValue(elementData.condominium_fee);
    setAdvertiser(elementData.advertiser?.name || '');
    setAdvertiserContact(elementData.advertiser?.phone || '');
    setPropertyLink(elementData.link || '');
    setDescription(elementData.description || '');
    setPinPlace({ lat: elementData.latitude, lng: elementData.longitude });
    if (elementData.land_area) setLandArea(elementData.land_area);
    if (errorReport.sample_images) setPictures(errorReport.sample_images);

    if (errorFindings) {
      setFindings(errorFindings);
    }
  }, [elementData]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  const verifyCep = async (cep: string): Promise<void> => {
    const addressData = await handleSearchCep(cep);
    if (addressData) {
      setCep(addressData.cep);
      setCity(addressData.city);
      setDistrict(addressData.district);
      setAddress(addressData.address);
      setUf(addressData.uf);
      setIsAddressEdited(true);
    }
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (!pinPlace) {
      setSnackbarMessage('Confirme a localização no mapa');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    if (findings.length < 3) {
      setSnackbarMessage('Adicione observações!');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    const updatedElement = {
      zipcode: cep,
      street: address,
      number: addressNumber,
      address_complement: addressComplement,
      city,
      district,
      state: uf,
      area: privateArea,
      land_area: isIndependentStructure ? landArea : null,
      parking_spaces: parkingLots,
      bedrooms: rooms,
      sell_price: sellingPrice,
      condominium_fee: communityValue,
      advertiser: JSON.stringify({
        name: advertiser,
        phone: advertiserContact,
      }),
      link: propertyLink,
      latitude: pinPlace.lat,
      longitude: pinPlace.lng,
      facade_image: elementData.facade_image || false,
      property_type: propertyType,
      age,
      description,
    };

    const updateError = {
      findings,
      finished: true,
      sample_description: updatedElement,
    };

    try {
      const response = await updateErrorReport(reportId, updateError);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== 0)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('Dados alterados com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      navigate('/error-report');
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleActivateElement = async (): Promise<void> => {
    try {
      const response = await activateElementErrorReport(reportId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== 0)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('O elemento foi reativado!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      navigate('/error-report');
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleFinishElement = async (): Promise<void> => {
    try {
      const response = await finishElementErrorReport(reportId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== 0)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('O ticket foi finalizado!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      navigate('/error-report');
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <BoxContainer>
      <TitleBox>
        <TitleTypography>
          {IconWarningMS}
          {Constants.reportedError}
        </TitleTypography>
        <FlexBox>
          <CreatedStyle>
            {Constants.creation}{' '}
            {errorReport.created_at && convertDate(errorReport.created_at)}
          </CreatedStyle>
          <OSNumber>
            {IconInventoryMS}
            {Constants.osNumber} {errorReport.reference_number}
          </OSNumber>
        </FlexBox>
      </TitleBox>
      <Grid container spacing={2} sx={{ marginBottom: '40px' }}>
        <FlexGrid item xs={5}>
          <Typography>{Constants.reportedBy}</Typography>
          <Typography fontWeight="bold">{errorReport.user_name}</Typography>
        </FlexGrid>
        <FlexGrid item xs={7}>
          <Typography>{Constants.client}</Typography>
          <Typography fontWeight="bold">
            {errorReport.requester_name}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={5}>
          <Typography>{Constants.goal}</Typography>
          <Typography fontWeight="bold">
            {errorReport.report_goal &&
              convertReportGoal(errorReport.report_goal)}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={7}>
          <Typography>{Constants.source}</Typography>
          <Typography fontWeight="bold">
            {errorReport.report_source &&
              convertReportSource(errorReport.report_source)}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={8}>
          <Typography>{Constants.errorDescription}</Typography>
          <DescriptionTypography>
            {errorReport.error_description}
          </DescriptionTypography>
        </FlexGrid>
        <FlexGrid item xs={4}>
          <OutlinedButton
            disabled={errorReport.finished}
            onClick={handleActivateElement}
          >
            {Constants.noErrorButton}
          </OutlinedButton>
        </FlexGrid>
        {errorReport.finished && (
          <FlexGrid item xs={12}>
            <Typography>{Constants.status}</Typography>
            <Typography color="primary.light" fontWeight="bold">
              finalizado
            </Typography>
          </FlexGrid>
        )}
      </Grid>
      <Divider />
      {elementData.updated_by_human && (
        <DescriptionTypography mt="20px">
          Este elemento já foi atualizado, verifique se o erro já foi corrigido.
        </DescriptionTypography>
      )}
      <TitleBox>
        <TitleTypography>
          {IconFactCheckMS}
          {Constants.element}
        </TitleTypography>
      </TitleBox>
      <Grid
        container
        spacing={2}
        component="form"
        id="edit-element"
        onSubmit={handleSubmit}
      >
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <PatternFormat
                required
                disabled={errorReport.finished}
                format="#####-###"
                customInput={StyledTextField}
                id="cep"
                label="CEP"
                color="secondary"
                value={cep}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  verifyCep(e.target.value)
                }
              />
            </Grid>
            <Grid item xs={9}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="address"
                label="logradouro"
                value={address}
                setValue={setAddress}
                onChange={() => setIsAddressEdited(true)}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="address-number"
                label="número"
                value={addressNumber}
                setValue={setAddressNumber}
                onChange={() => setIsAddressEdited(true)}
              />
            </Grid>
            <Grid item xs={5}>
              <CustomTextField
                disabled={errorReport.finished}
                id="address-complement"
                label="complemento"
                value={addressComplement}
                setValue={setAddressComplement}
              />
            </Grid>
            <Grid item xs={4}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="city"
                label="cidade"
                value={city}
                setValue={setCity}
                onChange={() => setIsAddressEdited(true)}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="uf"
                label="estado"
                value={uf}
                setValue={setUf}
                onChange={() => setIsAddressEdited(true)}
              />
            </Grid>
            <Grid item xs={5}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="district"
                label="bairro"
                value={district}
                setValue={setDistrict}
                onChange={() => setIsAddressEdited(true)}
              />
            </Grid>
            <Grid item xs={4}>
              <SelectTextField
                disable={errorReport.finished}
                id="property-type"
                label="tipo do imóvel"
                value={propertyType}
                setValue={setPropertyType}
                selectOptions={selectPropertyType()}
              />
            </Grid>
            <Grid item xs={isIndependentStructure ? 3 : 6}>
              <NumericTextField
                disable={errorReport.finished}
                id="total-area"
                label={
                  isPrivateUnit
                    ? Constants.privateArea
                    : Constants.constructedArea
                }
                suffix=" m²"
                decimalSeparator=","
                decimalScale={2}
                maxLength={18}
                value={privateArea}
                setValue={setPrivateArea}
              />
            </Grid>
            {isIndependentStructure && (
              <Grid item xs={4}>
                <NumericTextField
                  disable={errorReport.finished}
                  id="land-area"
                  label={Constants.landArea}
                  suffix=" m²"
                  decimalSeparator=","
                  decimalScale={2}
                  maxLength={18}
                  value={landArea}
                  setValue={setLandArea}
                />
              </Grid>
            )}
            <Grid item xs={isIndependentStructure ? 4 : 6}>
              <NumericTextField
                disable={errorReport.finished}
                id="parking-lots"
                label="vagas de garagem"
                suffix=" vagas"
                maxLength={9}
                value={parkingLots}
                setValue={setParkingLots}
              />
            </Grid>
            <Grid item xs={3}>
              <NumericTextField
                disable={errorReport.finished}
                id="rooms"
                label="quartos*"
                suffix=" quartos"
                maxLength={12}
                value={rooms}
                setValue={setRooms}
              />
            </Grid>
            <Grid item xs={5}>
              <PreffixNumericTextField
                disabled={errorReport.finished}
                id="community-value"
                label="valor do condomínio"
                prefix="R$ "
                decimalSeparator=","
                decimalScale={2}
                maxLength={18}
                value={communityValue}
                setValue={setCommunityValue}
              />
            </Grid>
            <Grid item xs={4}>
              <PreffixNumericTextField
                disabled={errorReport.finished}
                id="selling-price"
                label="preço de venda"
                prefix="R$ "
                decimalSeparator=","
                decimalScale={2}
                maxLength={18}
                value={sellingPrice}
                setValue={setSellingPrice}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextField
                required
                disabled={errorReport.finished}
                id="advertiser"
                label="anunciante"
                value={advertiser}
                setValue={setAdvertiser}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                required
                disabled={errorReport.finished}
                id="advertiser-contact"
                label="contato anunciante"
                value={advertiserContact}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setAdvertiserContact(formatPhone(e.target.value))
                }
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={3}>
              <NumericTextField
                disable={errorReport.finished}
                id="property-age-estimate"
                label="idade do imóvel"
                suffix=" anos"
                maxLength={9}
                value={age}
                setValue={setAge}
              />
            </Grid>
            <Grid item xs={9}>
              <CustomTextField
                disabled={errorReport.finished}
                id="property-link"
                label="link do imóvel"
                value={propertyLink}
                setValue={setPropertyLink}
              />
            </Grid>
          </Grid>
        </Grid>
        {pinPlace && (
          <Grid item xs={12} lg={6}>
            <StandardMap
              searchMap={searchMap}
              address={`${address} ${addressNumber}`}
              district={district}
              city={city}
              uf={uf}
              pinPlace={pinPlace}
              setPinPlace={setPinPlace}
            />
            <CoordContainer>
              <FlexBox>
                <BoldTypography>{Constants.lat}</BoldTypography>
                <Typography>{pinPlace.lat}</Typography>
              </FlexBox>
              <FlexBox>
                <BoldTypography>{Constants.lng}</BoldTypography>
                <Typography>{pinPlace.lng}</Typography>
              </FlexBox>
            </CoordContainer>
            <MapSearchButton
              cep={cep}
              address={address}
              addressNumber={addressNumber}
              district={district}
              city={city}
              uf={uf}
              hasSearched={hasSearched}
              isAddressEdited={isAddressEdited}
              disabled={errorReport.finished}
              labels={{
                searchMap: Constants.searchMap,
                mapUpdated: Constants.mapUpdated,
                fieldsIncomplete: Constants.fieldsIncomplete,
              }}
              onClick={() => {
                setSearchMap(!searchMap);
                setIsAddressEdited(false);
                setHasSearched(true);
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <MultilineTextField
            disabled={errorReport.finished}
            id="property-description"
            label="descrição do imóvel"
            value={description}
            setValue={setDescription}
          />
        </Grid>
        <Grid item xs={12}>
          <DownloadButton
            disabled={!elementData.pdf_url || errorReport.finished}
            onClick={() =>
              elementData.pdf_url &&
              handleDownloadFile(elementData.pdf_url, `${elementData.pdf}`)
            }
          >
            {Constants.downloadPdf}
          </DownloadButton>
        </Grid>
      </Grid>
      {pictures.length > 0 && (
        <SectionBox>
          <SectionTitle>
            {IconPhotoLibraryMS}
            {Constants.pictures}
          </SectionTitle>
          <Grid container spacing={1}>
            {pictures.slice(0, 5).map((pic) => (
              <Grid item xs={2} key={pic.url}>
                <StyledCardMedia image={pic.url} title={pic.caption} />
              </Grid>
            ))}
            <Grid item xs={2}>
              <AllPicturesSlider images={pictures} />
            </Grid>
          </Grid>
        </SectionBox>
      )}
      <SectionBox>
        <SectionTitle mb="20px">
          {IconEditDocumentMS}
          {Constants.findings}
        </SectionTitle>
        <MultilineTextField
          disabled={errorReport.finished}
          id="findings"
          label="observações"
          value={findings}
          setValue={setFindings}
        />
      </SectionBox>
      <SubmitBox sx={{ justifyContent: 'space-between' }}>
        <FinishElementDialog
          disable={errorReport.finished}
          modalCallback={handleFinishElement}
        />
        <Button
          disabled={errorReport.finished}
          form="edit-element"
          type="submit"
        >
          {Constants.save}
        </Button>
      </SubmitBox>
    </BoxContainer>
  );
}
